import { styled } from "@mui/material/styles";
import { Tab, TabProps, Tabs, TabsProps } from "./components/ui/Tabs";
import { SelectField } from "./components/ui/Select";
import { TextField } from "./components/ui/TextField";
import { tokens } from "./theme/tokens";
import { formControlHeight } from "./theme/form-control-height";

export const ListItemStyled = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
});

type MainListItemStyledProps = {
  active?: boolean;
};

export const MainListItemStyled = styled(ListItemStyled, {
  // Configure which props should be forwarded on DOM
  shouldForwardProp: (prop) => prop !== "active",
})<MainListItemStyledProps>(({ theme, active }) => ({
  margin: "6px 12px",
  padding: "14px 18px",
  border: active
    ? `1px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  // Clips the progress wash to the rounded corners. The wash is absolutely
  // positioned with square corners, so without this it escapes the curve and
  // leaves a hard edge at each end of the row.
  overflow: "hidden",
  // Surface, not transparent: on a warm ground an unfilled row is just an
  // outline on beige, so nothing reads as a card.
  background: theme.palette.surface,
  boxShadow: active ? "none" : "0 1px 2px rgba(20, 18, 15, 0.04)",

  "&:hover": {
    borderColor: theme.palette.primary.main,
    boxShadow: "0 2px 8px rgba(20, 18, 15, 0.06)",
  },
}));

type SubcategoryListItemStyledProps = {
  actionable?: boolean;
};

export const SubcategoryListItemStyled = styled(ListItemStyled, {
  // Configure which props should be forwarded on DOM
  shouldForwardProp: (prop) => prop !== "actionable",
})<SubcategoryListItemStyledProps>(({ theme, actionable }) => ({
  height: "48px",
  margin: "6px 12px",
  padding: "8px 20px 8px 36px",
  // `divider` rather than `text.disabled`: a border is a hairline, not
  // disabled text, and the two diverged once the palette gained real tokens.
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",

  "&:hover": {
    borderColor: actionable
      ? theme.palette.primary.main
      : theme.palette.divider,
  },
}));

interface ProminentButtonProps {
  textCenter?: boolean;
  disabled?: boolean;
  color?: "primary" | "error";
  outline?: boolean;
  small?: boolean;
}

export const ProminentButtonStyled = styled("div")<ProminentButtonProps>(({
  theme,
  textCenter,
  disabled,
  color = "primary",
  outline = false,
  small = false,
}) => {
  const palette = theme.palette[color];

  // Determine colors based on disabled and outline flags
  let textColor: string;
  let bgColor: string;
  let borderColor: string;

  if (disabled) {
    // Disabled overrides outline. This is the state a user sees every time a
    // form dialog opens, so it has to read as a button waiting for input
    // rather than as empty space.
    //
    // `text.secondary` on `divider` is about 4.5:1 — muted but unmistakably a
    // label. MUI's own `action.disabled` on `action.disabledBackground` is
    // grey on grey, and `text.disabled` on `divider` is worse still at roughly
    // 1.5:1, because both are near-identical warm greys.
    textColor = theme.palette.text.secondary;
    bgColor = theme.palette.divider;
    borderColor = theme.palette.divider;
  } else if (outline) {
    // outline variant
    textColor = palette.main;
    bgColor = "transparent";
    borderColor = palette.main;
  } else {
    // filled variant: borderless (border matches the fill) for a cleaner look
    textColor = palette.contrastText;
    bgColor = palette.main;
    borderColor = palette.main;
  }

  return {
    padding: small ? "6px 14px" : "8px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: textCenter ? "center" : "flex-start",
    textAlign: textCenter ? "center" : "left",
    height: small ? "36px" : "40px",
    fontSize: small ? "14px" : "16px",

    color: textColor,
    background: bgColor,
    border: `1px solid ${borderColor}`,
    borderRadius: "10px",

    cursor: disabled ? "not-allowed" : "pointer",
    pointerEvents: disabled ? "none" : "auto",
    // No blanket fade when disabled. The colours above already say "off", and
    // halving their opacity on top of that was what made the label vanish
    // into its own fill.
    opacity: 1,

    /*
     * Guarded, because on a touch screen `:hover` sticks.
     *
     * A phone applies hover to whatever sits under the last tap and leaves it
     * there until you tap elsewhere. The "Paid by" menu opens over the bottom
     * of the expense sheet, so choosing an option leaves the pointer resting
     * on the Create button underneath — and pine at 70% is a washed-out sage
     * that reads as disabled. Nothing was disabled; the button was stuck in a
     * state that device should never have entered.
     */
    "@media (hover: hover) and (pointer: fine)": {
      "&:hover": disabled
        ? {}
        : {
            opacity: 0.7,
          },
    },
  };
});

/**
 * The main content column. Also caps the width: rows are `space-between`, so
 * on a wide monitor the label sat against one edge of the viewport and its
 * amount against the other, with a metre of empty paper between them.
 *
 * The bottom margin used to reserve 68px for a fixed footer holding only the
 * feedback link. That link now lives in the account menu, so on desktop this
 * is just breathing room at the end of a list.
 *
 * On phones it clears the fixed bottom toolbar. The reservation belongs here
 * rather than in the toolbar itself: `ActionsBar` renders at the top of every
 * page, so a spacer inside it held the space at the top of the scroll — the
 * one place it was not needed.
 */
export const ContentWrapperStyled = styled("div")(({ theme }) => ({
  marginBottom: "32px",
  maxWidth: `${tokens.contentMaxWidth}px`,
  marginInline: "auto",

  [theme.breakpoints.down("sm")]: {
    marginBottom: "calc(72px + env(safe-area-inset-bottom, 0px))",
  },
}));

/**
 * Holds the previous month on screen while the next one loads, dimmed just
 * enough to read as pending.
 *
 * Changing month changes the query variables, so Apollo has no data for them
 * and the list used to collapse to three skeleton rows before re-expanding to
 * a dozen. That height collapse was the jumpiness — a transition would only
 * have animated the jump.
 */
export const RefreshingStyled = styled("div", {
  shouldForwardProp: (prop) => prop !== "refreshing",
})<{ refreshing?: boolean }>(({ refreshing }) => ({
  opacity: refreshing ? 0.55 : 1,
  transition: "opacity 150ms ease",
  // The old month is still painted, so it must not accept clicks that would
  // act on the month being navigated away from.
  pointerEvents: refreshing ? "none" : "auto",
}));

/** An amount that is good news: under budget, a gain. */
export const PositiveAmountStyled = styled("span")(({ theme }) => ({
  color: theme.palette.money.positive,
}));

/**
 * An amount that is bad news: over budget, a loss.
 *
 * Named for what it means rather than for a severity level. These were
 * `ErrorTextStyled` and `WarningTextStyled`, and under-budget — unambiguously
 * good news — was rendered with the warning one.
 */
export const NegativeAmountStyled = styled("span")(({ theme }) => ({
  color: theme.palette.money.negative,
}));
export const UnderlineTextStyled = styled("span")(({ theme }) => ({
  fontWeight: "500",
  borderBottom: `1px dotted ${theme.palette.text.primary}`,
}));

// ---- Shared page layout primitives (Reports, Settings, ...) ----

export const PageWrapperStyled = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const HelperTextStyled = styled("p")(({ theme }) => ({
  margin: 0,
  color: theme.palette.text.secondary,
  fontSize: tokens.fontSize.sm,
}));

// Stacked + full-width on mobile; inline on larger screens with a consistent
// minimum button width so actions line up evenly (labels can still grow).
export const ButtonGroupStyled = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    "& > *": {
      minWidth: 160,
    },
  },
}));

export const TabsStyled = styled((props: TabsProps) => <Tabs {...props} />)(
  ({ theme }) => ({
    marginLeft: theme.spacing(2),
    minHeight: theme.spacing(6),
    "& .MuiTabs-indicator": {
      height: 2,
      borderRadius: 2,
      backgroundColor: theme.palette.primary.main,
    },
  }),
);

export const TabStyled = styled((props: TabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: "none",
  minHeight: theme.spacing(4),
  padding: "8px 16px",
  fontSize: tokens.fontSize.md,
  fontWeight: "normal",
  borderRadius: "10px",
  marginRight: theme.spacing(2),
  // Unselected sits on the page ground, so it takes ink — `primary.contrastText`
  // means "text on the accent fill" and is light.
  background: "transparent",
  color: theme.palette.text.secondary,

  "&.Mui-selected": {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  // The background change is harmless on touch; the fade is not, so the whole
  // rule is guarded rather than split in two.
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": {
      opacity: 0.8,
      background: theme.palette.action.hover,
      "&.Mui-selected": {
        background: theme.palette.primary.main,
      },
    },
  },
}));

/**
 * A select, matching the text fields it sits beside in every form.
 *
 * It used to be a solid `primary.main` block with `primary.contrastText` text
 * and border — written when the accent was a light mint, where that read as a
 * tinted control. Once the accent became dark pine it became a near-black slab
 * in the middle of a white dialog, and worse: the floating label lives in the
 * notch cut out of the outline, and behind that notch is the field's own
 * background. A dark label over a dark fill is what made "Paid by" and
 * "Subcategory" look like the label was printed on top of the control.
 *
 * Same family as the `primary.contrastText`-used-as-ink bugs elsewhere in this
 * palette migration: syntactically fine, semantically inverted.
 *
 * So it now inherits the theme's own outlined input — surface, hairline
 * border, ink text — and only keeps the radius and height that put it on the
 * same baseline as `TextFieldStyled`.
 */
export const SelectStyled = styled(SelectField)(({ theme }) =>
  formControlHeight(theme),
);

export const TextFieldStyled = styled(TextField)(({ theme }) => ({
  marginBottom: 0,
  ...formControlHeight(theme),
}));

/**
 * Two controls on one line, where neither needs the full width.
 *
 * A form of five stacked full-width rows pushes the action button under the
 * keyboard and gives a date — which is almost always today — the same weight as
 * the amount. Pairing by the room a value actually needs gets the whole form
 * above the fold.
 *
 * `flex-basis` rather than a percentage, so a pair that cannot fit side by side
 * stacks on its own instead of being squeezed. On a 320px phone the date and
 * its calendar button need more than half the row, and they wrap.
 */
export const FormRowStyled = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),

  "& > *": {
    flex: "1 1 150px",
    // Without this a long subcategory name refuses to shrink and pushes its
    // neighbour off the row.
    minWidth: 0,
  },
}));
